import React from "react";
import { useMediaQuery } from "react-responsive";
import { auth } from "../../api/firebase";
import { Language, LANGUAGE_OPTIONS, termsUrl, useSchoolLangs } from "../../api/Lang";
import YesNoDlg from "../../api/YesNoDlg";
import SettingsHook from "../../logic/SettingsHook";
import { Select, MenuItem, FormControl, Button } from '@mui/material';
import { useSettingsStrings } from "./settings-strings";
import { useFeedbackStrings } from "../feedback-strings";

type SettingsProps = {
    onOpenFeedback: () => void;
};

export default function Settings({ onOpenFeedback }: SettingsProps) {

    const settingsStrings = useSettingsStrings();
    const feedbackStrings = useFeedbackStrings();
    const { langs: applicableLangs } = useSchoolLangs();
    const { commonStrings, lang, setLang, dlgSignOut, setDlgSignOut, signOutDlgTitle, onSignOut, onDeleteAccount } = SettingsHook();
    const langValue = applicableLangs.includes(lang) ? lang : '';
    const isMobileLayout = useMediaQuery({ query: "(max-width: 930px)" });
    const logoutCardBody = isMobileLayout ? settingsStrings.logoutCardBodyMobile : settingsStrings.logoutCardBody;

    return <div className='settings'>

        {dlgSignOut &&
            <YesNoDlg title={signOutDlgTitle} msg={commonStrings.areYouSure} yes={signOutDlgTitle} no={commonStrings.cancel} onNo={() => setDlgSignOut(false)} onYes={() => { void auth.signOut().catch((err) => console.error('Sign out failed:', err)); }} />
        }
        <div className="rectangle settings-feedback-card">
            <div className="settings-feedback-card__intro">
                <img
                    src="/img-assets/feedback.svg"
                    alt=""
                    className="settings-feedback-card__icon"
                />
                <div>
                    <div className="settings-feedback-card__heading">{feedbackStrings.pageHeading}</div>
                    <p className="settings-feedback-card__body">{settingsStrings.feedbackCardBody}</p>
                </div>
            </div>
            <div className="separator" />
            <button
                type="button"
                className="settings-feedback-card__action"
                onClick={onOpenFeedback}
            >
                {feedbackStrings.navLabel}
                <img src="/img-assets/arrow-right.svg" alt="" />
            </button>
        </div>
        <div className='rectangle'>
            <label className="content user-role" style={{ padding: "10px 4px 10px 16px" }}>
                <div className='input-label'>{settingsStrings.languageLabel}</div>
                <div className="dropdown-cats">
                    <FormControl fullWidth>
                        <Select
                            value={langValue}
                            onChange={(e) => setLang(e.target.value as Language)}
                            style={{ padding: '4px 0px 10px' }}
                            disableUnderline
                            variant="standard"
                            IconComponent={(props) => (
                                <img
                                    src="/img-assets/arrow-down.svg"
                                    style={{
                                        ...props.style,
                                        color: '#01ABAE',
                                        marginRight: '20px'
                                    }}
                                />
                            )}
                            renderValue={(value) => {
                                if (!value || !(value in LANGUAGE_OPTIONS)) {
                                    return '';
                                }
                                const opt = LANGUAGE_OPTIONS[value as Language];
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={opt.flagIcon}
                                            style={{ marginRight: '8px', width: '20px', height: '15px' }}
                                        />
                                        <span>{opt.nativeName}</span>
                                    </div>
                                );
                            }}
                        >
                            {applicableLangs.map((code) => (
                                <MenuItem key={code} value={code}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={LANGUAGE_OPTIONS[code].flagIcon} style={{ marginRight: '8px', width: '20px', height: '15px' }} />
                                        <span>{LANGUAGE_OPTIONS[code].nativeName}</span>
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </label>
        </div>
        <div className='rectangle'>
            <a href={termsUrl('terms', lang)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", cursor: 'context-menu' }}>
                <div className='label-top' >
                    {settingsStrings.termsConditions}
                    <img src="/img-assets/arrow-right.svg" style={{ float: "right" }} />
                </div>
            </a>
            <div className='separator'></div>
            <a href={termsUrl('privacy', lang)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", cursor: 'context-menu' }}>
                <div className='label-bottom'>
                    {settingsStrings.privacyPolicy}
                    <img src="/img-assets/arrow-right.svg" style={{ float: "right" }} />
                </div>
            </a>
            <div className='separator'></div>
            <a href={termsUrl('cookies', lang)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", cursor: 'context-menu' }}>
                <div className='label-bottom'>
                    {settingsStrings.cookiesPolicy}
                    <img src="/img-assets/arrow-right.svg" style={{ float: "right" }} />
                </div>
            </a>
        </div>

        <div className='rectangle' >
            <div className='label-top' onClick={() => window.open(termsUrl('instructions', lang), '_blank')}>
                <img src="/img-assets/how-it-works.svg" style={{ marginRight: "8px", verticalAlign: "middle" }} />
                {settingsStrings.howItWorks}
                <img src="/img-assets/arrow-right.svg" style={{ float: "right" }} />
            </div>
        </div>

        <div className="rectangle settings-logout-card">
            <div className="settings-logout-card__intro">
                <img
                    src="/img-assets/logout.svg"
                    alt=""
                    className="settings-logout-card__icon"
                />
                <div className="settings-logout-card__copy">
                    <div className="settings-logout-card__heading">{settingsStrings.logout}</div>
                    <p className="settings-logout-card__body">{logoutCardBody}</p>
                    <Button
                        type="button"
                        variant="text"
                        size="small"
                        onClick={onSignOut}
                        sx={{
                            alignSelf: 'flex-end',
                            mt: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '16px',
                            letterSpacing: '0.28px',
                        }}
                    >
                        {settingsStrings.logout}
                    </Button>
                </div>
            </div>
            <div className='separator' style={{ display: 'none' }}></div>
            <div className='label-bottom' style={{ display: 'none' }} onClick={onDeleteAccount} >
                {settingsStrings.unregister}
            </div>
        </div>
    </div>
}